import { withMask } from 'use-mask-input';
import type { ChangeEvent, ClipboardEvent } from 'react';

type CardInputChangeCallback = (value: string) => void;

const CARD_MASK = '9999 9999 9999 9999';
const CARD_MAX_LENGTH = 16;

const unformatCardNumber = (value: string): string => {
  return value.replace(/\s/g, '');
};

export const createCardInputRef = (): ((
  node: HTMLInputElement | null
) => void) => {
  return withMask(CARD_MASK, { placeholder: '' });
};

export const createCardInputHandlers = (
  onValueChange: CardInputChangeCallback
) => {
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;

    const target = event.currentTarget;
    const digits = pasted.replace(/\D/g, '').slice(0, CARD_MAX_LENGTH);

    if (digits && target) {
      const currentValue = unformatCardNumber(target.value);
      const selectionStart = target.selectionStart ?? currentValue.length;
      const selectionEnd = target.selectionEnd ?? selectionStart;

      const newValue =
        currentValue.slice(0, selectionStart) +
        digits +
        currentValue.slice(selectionEnd);

      target.value = newValue.slice(0, CARD_MAX_LENGTH);
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const formattedValue = target.value;
    const unformattedValue = unformatCardNumber(formattedValue);
    const limitedValue = unformattedValue.slice(0, CARD_MAX_LENGTH);

    onValueChange(limitedValue);
  };

  return {
    paste: handlePaste,
    change: handleChange,
  };
};
